import React, { ReactElement, useEffect, useCallback } from 'react'
import WebSettingsCard from 'live/components/ui/admin/web-settings-card'
import { useWebSettingsState } from 'live/settings/useWebSettingsState'
import globalNotification from 'live/services/global-notification'
import FormGroup from 'live/components/forms/ui/form-group'
import ActionToggle from 'live/components/ui/action-toggle'
import i18n from 'live/services/i18n'
import  getCustomSendSmtpDelay  from '../js/api/getSendSmtpDelay'
import setCustomSendSmtpDelay from '../js/api/setSendSmtpDelay'
import { ResponseType } from '../js/common/types/api/request'
import getOneEmailForRecipientGroupFlag from '../js/api/getOneEmailForRecipientGroupFlag'
import setOneEmailForRecipientGroupFlag from '../js/api/setOneEmailForRecipientGroupFlag'


interface basicReportsMakerSettings {
    customSendSmtpDelay: number
    oneEmailForRecipientGroupFlag: boolean
}

interface ReportsMakerSettings extends basicReportsMakerSettings{
    originalValues?: basicReportsMakerSettings
}

function ReportsMakerSettingsPage(): ReactElement{
    const [reportsMakerSettings, setReportsMakerSettings] = React.useState<ReportsMakerSettings>({
        customSendSmtpDelay: -1,
        oneEmailForRecipientGroupFlag: false
    })

    const { state, actions } = useWebSettingsState(reportsMakerSettings)

    const fetchItems = useCallback(() => {
        Promise.all([
            getCustomSendSmtpDelay(),
            getOneEmailForRecipientGroupFlag()
        ]).then(([currentCustomDelay, oneEmailForRecipientGroupFlag]) => {
            const config = {
                customSendSmtpDelay: currentCustomDelay,
                oneEmailForRecipientGroupFlag: oneEmailForRecipientGroupFlag
            }
            return {
                ...config,
                originalValues: config
            }
            
        }).then(config => {
            setReportsMakerSettings(config)
            actions.setPersistedData(config)  
        }).catch(err => {
            globalNotification.error(`${i18n('Failure loading reports-maker settings')}. ${err.message}`)
        })
    }, [actions])

    useEffect(fetchItems, [])

    const onSave = React.useCallback(() => {
        actions.submit()
        persist(reportsMakerSettings)
            .then(responses => {
                if(responses.length === 1)
                    return responses[0]

                const failResponses = responses.filter(response => !response.success)
                if(failResponses.length > 0){
                    console.error(failResponses.map(response => response.message))
                    return {
                        success: false,
                        message: i18n("check the console for more information"),
                        error: i18n("check the console for more information")
                    } as ResponseType
                }
                return {
                    success: true
                } as ResponseType
            })
            .then(response => {
                if(response.success){
                    actions.saveSuccess(reportsMakerSettings)
                    globalNotification.success(i18n('Settings successfully saved'))
                }else{
                    const msg = `${i18n('Failure saving settings')}. ${response.message}`
                    actions.setError(msg)
                    globalNotification.error(msg)
                }
            })
            .catch(async r => {
                actions.setError(await r.text())
            })
    }, [actions, reportsMakerSettings])

    return (
        <WebSettingsCard
            name="reports-maker-settings"
            className="live-feature mb-15"
            open={state.open}
            error={state.submitted ? state.error : null}
            saved={state.open && state.saved}
            save={onSave}
            toggle={actions.toggle}
            onCancel={() => setReportsMakerSettings(state.persistedData)}
        >
             <div className="live-feature__header">
                <h2 className="flex-1">{i18n('Current custom SMTP send delay')}</h2>
            </div>
            
            <FormGroup
                help={i18n(
                    "This is the interval between each email, that will be sent from reports-maker to the SMTP integration. The default value is 1800ms"
                )}
                value={reportsMakerSettings.customSendSmtpDelay}
                hideInput={!state.open}
                onChange={({ target }) => setReportsMakerSettings(s => ({ ...s, customSendSmtpDelay: target.value }))}
            />
            <div style={{marginBottom: "25px"}}>
                <ActionToggle 
                    name="web-settings-reportsmaker-disableOneEmailForRecipientGroups"
                    onChange={() => setReportsMakerSettings(s => ({ ...s, oneEmailForRecipientGroupFlag: !reportsMakerSettings.oneEmailForRecipientGroupFlag }))}
                    elLeft={<span>on</span>}
                    elRight={<span>off</span>}
                    active={reportsMakerSettings.oneEmailForRecipientGroupFlag}
                    disabled={!state.open}
                />
                <span>{
                    i18n("Send all Recipient Group recipients in the same email as BCC.")
                }</span>
            </div>
        </WebSettingsCard>
    )
}

const persist = (config: ReportsMakerSettings): Promise<ResponseType[]> => {
    const promises = [] as Promise<ResponseType>[]
    if(config.customSendSmtpDelay !== config.originalValues?.customSendSmtpDelay)
        promises.push(setCustomSendSmtpDelay(config.customSendSmtpDelay))
    if(config.oneEmailForRecipientGroupFlag !== config.originalValues?.oneEmailForRecipientGroupFlag)
        promises.push(setOneEmailForRecipientGroupFlag(config.oneEmailForRecipientGroupFlag))
    
    return Promise.all(promises)
}

export default {
    persist: persist,
    configComponent: ReportsMakerSettingsPage,
    section: i18n('Reports maker'),
    name: i18n('Reports maker settings')
}